import { InvoiceDialogComponent } from '@/shared/invoice-dialog.component';
import { ColDef, GridReadyEvent } from '@ag-grid-community/core';
import { Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  DocSubmitTypeEnum,
  InvoiceDetail,
  InvoiceHeader,
  InvoiceStatus,
  ItemDetail,
  ItemDetailStatusEnum,
  JoHeader,
} from 'app/model/entities/entity-model';
import { InvoiceStatusCode } from 'app/model/enums/invoice-status-code';
import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';
import { DateFns } from 'app/shared/date-fns';
import { DialogBaseComponent } from 'app/shared/dialog-base.component';
import { DomainBaseComponent, wrapBusy } from 'app/shared/domain-base.component';
import { DomainService } from 'app/shared/domain.service';
import { MenuAction } from 'app/shared/menu-renderer.component';
import { NavFns } from 'app/shared/nav-fns';
import { QbHealthService } from 'app/shared/qb-health.service';
import { UnitOfWork } from 'app/shared/unit-of-work';
import { UtilFns } from 'app/shared/util-fns';
import _ from 'lodash';
import { takeUntil } from 'rxjs/operators';

import { FreightEntryDialogComponent, FreightEntryDialogParams } from './freight-entry-dialog.component';
import {
  InvoicePostAction,
  InvoiceProcessingService,
  StepStatus,
} from './invoice-processing.service';

@Component({
  selector: 'app-acct-owned-invoices',
  templateUrl: './acct-owned-invoices.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AcctOwnedInvoicesComponent extends DomainBaseComponent {
  actionableGridOptions!: GridOptionsExt;
  actionableActions: InvoicePostAction[] = [];

  historyGridOptions!: GridOptionsExt;
  historyInvoices: InvoiceHeader[] = [];

  selectedInvoiceHeader!: InvoiceHeader;
  invoiceDetails: InvoiceDetail[] = [];

  private isProcessing = false;

  constructor(
    public override uow: UnitOfWork,
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    public qbHealth: QbHealthService,
    private invoiceProcessing: InvoiceProcessingService,
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => this.updateFromContext());
  }

  async updateFromContext() {
    this.actionableGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onActionableGridReady,
      onRowSelected: this.onActionableRowSelected,
      getRowId: (params) => params.data?.invoiceHeader?.id?.toString(),
    });

    this.historyGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onHistoryGridReady,
      onRowSelected: this.onHistoryRowSelected,
    });

    this.isPageReady = true;
    await this.qbHealth.refresh();
  }

  // -- Actionable grid --

  onActionableGridReady = (event: GridReadyEvent) => {
    const colDefs: ColDef[] = [
      {
        colId: 'actions',
        ...AgFns.createMenuProps('', this.getMenuActions(), { buttonLabel: 'Actions' }),
        ...AgFns.fixWidth(130),
      },
      {
        ...AgFns.createButtonProps('', this.processInvoice.bind(this), {
          calcLabel: () => 'Process',
        }),
        filter: 'agTextColumnFilter',
        field: 'invoiceHeader.id',
        headerName: 'Invoice',
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Account',
        field: 'invoiceHeader.joHeader.account.accountName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'GL Account Ref',
        field: 'invoiceHeader.joHeader.account.gLAccountRef',
        valueFormatter: (params: any) => params.value || '— missing —',
        cellStyle: (params: any) =>
          !params.value ? { color: 'var(--p-red-500, #ef4444)', fontWeight: 600 } : null,
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Job Order',
        ...NavFns.createIdCellClickedNavProps(
          'invoiceHeader.joHeaderId',
          this.router,
          '/in-process/manage/job-orders',
        ),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Date',
        field: 'invoiceHeader.invoiceDate',
        filter: 'agDateColumnFilter',
        ...AgFns.fixWidth('date'),
      },
      {
        headerName: 'Subtotal',
        valueGetter: (p: any) => p.data?.invoiceHeader?.subtotalAmt,
        valueFormatter: (p: any) => (p.value != null ? UtilFns.fmtCurrency(p.value) : ''),
        type: 'numericColumn',
        ...AgFns.fixWidth(110),
      },
      {
        headerName: 'Freight',
        valueGetter: (p: any) => p.data?.invoiceHeader?.freightAmt ?? 0,
        valueFormatter: (p: any) => (p.value != null ? UtilFns.fmtCurrency(p.value) : ''),
        type: 'numericColumn',
        ...AgFns.fixWidth(100),
      },
      {
        headerName: 'Tax',
        valueGetter: (p: any) => p.data?.invoiceHeader?.taxAmt,
        valueFormatter: (p: any) => (p.value != null ? UtilFns.fmtCurrency(p.value) : ''),
        type: 'numericColumn',
        ...AgFns.fixWidth(100),
      },
      {
        headerName: 'Total',
        valueGetter: (p: any) => p.data?.amount,
        valueFormatter: (p: any) => (p.value != null ? UtilFns.fmtCurrency(p.value) : ''),
        type: 'numericColumn',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Deliver',
        ...AgFns.fixWidth(160),
        valueGetter: (p: any) => p.data,
        cellRenderer: (p: any) => this.renderDeliverCell(p.data),
        tooltipValueGetter: (p: any) => (p.data?.deliverStatus === 'error' ? p.data?.errorMessage : null),
      },
      {
        headerName: 'Save PDF',
        ...AgFns.fixWidth(100),
        valueGetter: (p: any) => p.data?.savePdfStatus,
        cellRenderer: (p: any) => this.renderPendingStepCell(p.data?.savePdfStatus, 'Save PDF'),
        tooltipValueGetter: (p: any) => (p.data?.savePdfStatus === 'error' ? p.data?.errorMessage : null),
      },
      {
        headerName: 'P-Card',
        ...AgFns.fixWidth(100),
        valueGetter: (p: any) => p.data?.pCardStatus,
        cellRenderer: (p: any) => this.renderPendingStepCell(p.data?.pCardStatus, 'Charge'),
        tooltipValueGetter: (p: any) => (p.data?.pCardStatus === 'error' ? p.data?.errorMessage : null),
      },
      {
        headerName: 'Post QB',
        ...AgFns.fixWidth(100),
        valueGetter: (p: any) => p.data?.postQbStatus,
        cellRenderer: (p: any) => this.renderPendingStepCell(p.data?.postQbStatus, 'Post'),
        tooltipValueGetter: (p: any) => (p.data?.postQbStatus === 'error' ? p.data?.errorMessage : null),
      },
      {
        headerName: 'Status',
        field: 'invoiceHeader.invoiceStatus.name',
        ...AgFns.fixWidth(120),
      },
    ];

    const sortModel = [{ colId: 'invoiceHeader.invoiceDate', sort: 'desc' as const }];
    AgFns.initGrid(event, colDefs, sortModel);
    this.loadData();
  };

  onHistoryGridReady = (event: GridReadyEvent) => {
    const colDefs: ColDef[] = [
      {
        ...AgFns.createButtonProps('Invoice', this.openInvoiceDialog.bind(this), {
          calcLabel: (data: InvoiceHeader) => data.id,
        }),
        filter: 'agTextColumnFilter',
        field: 'id',
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Account',
        field: 'joHeader.account.accountName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'Job Order',
        ...NavFns.createIdCellClickedNavProps('joHeaderId', this.router, '/in-process/manage/job-orders'),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Date',
        field: 'invoiceDate',
        filter: 'agDateColumnFilter',
        ...AgFns.fixWidth('date'),
      },
      {
        headerName: 'Subtotal',
        field: 'subtotalAmt',
        valueFormatter: (p: any) => (p.value != null ? UtilFns.fmtCurrency(p.value) : ''),
        type: 'numericColumn',
        ...AgFns.fixWidth(110),
      },
      {
        headerName: 'Freight',
        field: 'freightAmt',
        valueFormatter: (p: any) => (p.value != null ? UtilFns.fmtCurrency(p.value) : ''),
        type: 'numericColumn',
        ...AgFns.fixWidth(100),
      },
      {
        headerName: 'Tax',
        field: 'taxAmt',
        valueFormatter: (p: any) => (p.value != null ? UtilFns.fmtCurrency(p.value) : ''),
        type: 'numericColumn',
        ...AgFns.fixWidth(100),
      },
      {
        headerName: 'Total',
        field: 'totalAmt',
        valueFormatter: (p: any) => (p.value != null ? UtilFns.fmtCurrency(p.value) : ''),
        type: 'numericColumn',
        ...AgFns.fixWidth(140),
      },
      {
        ...AgFns.createSetFilterObjectProps(
          'Status',
          'invoiceStatus.name',
          this.dbQueryService.getAllCached(InvoiceStatus),
        ),
        ...AgFns.flexWidth(120),
      },
    ];

    const sortModel = [{ colId: 'invoiceDate', sort: 'desc' as const }];
    AgFns.initGrid(event, colDefs, sortModel);
  };

  // -- Data --

  @wrapBusy
  async loadData() {
    const [actionable, all] = await Promise.all([
      this.dbQueryService.getAccountOwnedInvoices('actionable'),
      this.dbQueryService.getAccountOwnedInvoices('posted'),
    ]);
    this.actionableActions = actionable.map((invh) => this.buildAction(invh));
    this.historyInvoices = all;
  }

  private buildAction(invh: InvoiceHeader): InvoicePostAction {
    const account = invh.joHeader.account;
    const hasPCard = !!invh.joHeader.pCardCustomerProfileId && !!invh.joHeader.pCardPaymentProfileId;
    const deliveryMethod = account.invoiceDocSubmitTypeId ?? DocSubmitTypeEnum.None;
    const needsDelivery =
      deliveryMethod === DocSubmitTypeEnum.Print ||
      deliveryMethod === DocSubmitTypeEnum.Email ||
      deliveryMethod === DocSubmitTypeEnum.PrintAndEmail;

    return {
      invoiceHeader: invh,
      deliveryMethod,
      shouldSaveToPdf: account.saveOutputToPdf,
      isManifested: false,
      manifestName: undefined,
      isAccountOwned: true,
      hasPCard,
      amount: invh.roundedTotalAmt(),

      deliverStatus: needsDelivery ? 'pending' : 'n/a',
      savePdfStatus: account.saveOutputToPdf ? 'pending' : 'n/a',
      pCardStatus: hasPCard ? 'pending' : 'n/a',
      postQbStatus: 'pending',

      overallStatus: 'pending',
      errorMessage: null,
      processedTs: null,
      subordinateActions: [],
    };
  }

  // -- Row selection (detail panel below the grid) --

  onActionableRowSelected = (e: any) => {
    if (!e.node.isSelected()) return;
    const action = e.data as InvoicePostAction;
    if (!action) return;
    this.selectedInvoiceHeader = action.invoiceHeader;
    this.invoiceDetails = action.invoiceHeader.invoiceDetails;
  };

  onHistoryRowSelected = (e: any) => {
    if (!e.node.isSelected()) return;
    const invh = e.data as InvoiceHeader;
    if (!invh) return;
    this.selectedInvoiceHeader = invh;
    this.invoiceDetails = invh.invoiceDetails;
  };

  // -- Menu / per-row actions --

  private hasGlAccountRef(action: InvoicePostAction): boolean {
    return !!action?.invoiceHeader?.joHeader?.account?.gLAccountRef;
  }

  getMenuActions(): MenuAction[] {
    return [
      {
        label: 'View Invoice',
        icon: 'pi pi-eye',
        action: (action: InvoicePostAction) => this.viewInvoice(action),
      },
      {
        label: 'Set Freight',
        icon: 'pi pi-dollar',
        action: (action: InvoicePostAction) => this.setFreight(action),
        canDisplay: (action: InvoicePostAction) =>
          !this.isProcessing && action.postQbStatus !== 'success',
      },
      { label: '', action: () => {}, separator: true },
      {
        label: 'Charge P-Card',
        icon: 'pi pi-credit-card',
        action: (action: InvoicePostAction) => this.chargePCard(action),
        canDisplay: (action: InvoicePostAction) =>
          this.hasGlAccountRef(action) &&
          this.qbHealth.isQbHealthy &&
          !this.isProcessing &&
          (action.pCardStatus === 'pending' || action.pCardStatus === 'error'),
      },
      {
        label: 'Deliver',
        icon: 'pi pi-send',
        action: (action: InvoicePostAction) => this.deliverInvoice(action),
        canDisplay: (action: InvoicePostAction) =>
          this.hasGlAccountRef(action) &&
          this.qbHealth.isQbHealthy &&
          !this.isProcessing &&
          !this.needsPCardCharge(action) &&
          (action.deliverStatus === 'pending' || action.deliverStatus === 'error'),
      },
      {
        label: 'Save PDF',
        icon: 'pi pi-file-pdf',
        action: (action: InvoicePostAction) => this.savePdf(action),
        canDisplay: (action: InvoicePostAction) =>
          this.hasGlAccountRef(action) &&
          this.qbHealth.isQbHealthy &&
          !this.isProcessing &&
          !this.needsPCardCharge(action) &&
          (action.savePdfStatus === 'pending' || action.savePdfStatus === 'error'),
      },
      {
        label: 'Post to QuickBooks',
        icon: 'pi pi-upload',
        action: (action: InvoicePostAction) => this.postToQb(action),
        canDisplay: (action: InvoicePostAction) =>
          this.hasGlAccountRef(action) &&
          this.qbHealth.isQbHealthy &&
          !this.isProcessing &&
          !this.needsPCardCharge(action) &&
          (action.postQbStatus === 'pending' || action.postQbStatus === 'error'),
      },
    ];
  }

  async viewInvoice(action: InvoicePostAction) {
    await this.openInvoiceDialog(action.invoiceHeader);
  }

  async openInvoiceDialog(invoiceHeader: InvoiceHeader) {
    await InvoiceDialogComponent.openDialog(this.domainService.pngDialogService, {
      invoiceId: invoiceHeader.id,
    });
  }

  // -- Freight step --

  /**
   * Opens the freight-entry dialog for one invoice. Returns true when the user
   * confirms with an amount (even $0.00), false when they cancel. On confirm,
   * writes freightAmt onto the header, recomputes totalAmt, saves.
   */
  private async promptFreight(action: InvoicePostAction): Promise<boolean> {
    const invh = action.invoiceHeader;
    const params: FreightEntryDialogParams = {
      invoiceId: invh.id,
      accountName: invh.joHeader.account.accountName,
      currentFreight: invh.freightAmt ?? 0,
      dialogWidth: '480px',
      dialogHeight: 'auto',
    };
    const result = await DialogBaseComponent.open(FreightEntryDialogComponent, this, params);
    if (result === null || result === undefined) return false;

    invh.freightAmt = +result;
    // Recompute totals — freight is not taxed and simply adds to the total.
    invh.totalAmt = invh.roundedTotalAmt();
    action.amount = invh.totalAmt ?? 0;

    if (this.dbSaveService.hasChanges()) {
      await this.dbSaveService.saveChanges();
    }
    return true;
  }

  async setFreight(action: InvoicePostAction) {
    const ok = await this.promptFreight(action);
    if (ok) {
      this.refreshActionableGrid();
    }
  }

  // -- Per-row processing --

  async processInvoice(action: InvoicePostAction) {
    if (this.isProcessing) return;
    if (!(await this.assertQbHealthy())) return;
    if (!this.hasGlAccountRef(action)) {
      await this.dialogService.okDialog(
        'Cannot Process',
        `Account "${action.invoiceHeader.joHeader.account.accountName}" is missing a QuickBooks GL Account reference. Set one before posting.`,
      );
      return;
    }

    // Freight prompt runs once per Process click, before the pipeline starts.
    const freightOk = await this.promptFreight(action);
    if (!freightOk) return;

    this.isProcessing = true;
    for (const a of [action]) {
      a.overallStatus = 'in-progress';
      a.errorMessage = null;
    }

    try {
      if (this.needsPCardCharge(action)) {
        await this.invoiceProcessing.chargePCardCore(action, () => this.refreshActionableGrid());
      }

      if (action.deliverStatus === 'pending' || action.deliverStatus === 'error') {
        await this.invoiceProcessing.deliverInvoiceCore(action, false, false, () => this.refreshActionableGrid());
      }

      if (action.savePdfStatus === 'pending' || action.savePdfStatus === 'error') {
        await this.invoiceProcessing.savePdfCore(action, () => this.refreshActionableGrid());
      }

      // Flush pending InvoiceHeader mutations (printedAt, pdfSavedAt) before
      // the server-side QB post; the server bumps RowVersion on its own save
      // and any leftover client changes would fail with a concurrency error.
      if (this.dbSaveService.hasChanges()) {
        await this.dbSaveService.saveChanges();
      }

      if (action.postQbStatus === 'pending' || action.postQbStatus === 'error') {
        await this.invoiceProcessing.postToQbCore(action, () => this.refreshActionableGrid());
      }

      action.overallStatus = 'success';
      action.processedTs = new Date();
      this.moveActionToHistory(action);
    } catch (err: any) {
      if (action.overallStatus === 'in-progress') {
        action.overallStatus = 'error';
        action.errorMessage = err?.message || 'Unknown error';
      }
      if (action.deliverStatus === 'in-progress') action.deliverStatus = 'error';
      if (action.savePdfStatus === 'in-progress') action.savePdfStatus = 'error';
      if (action.pCardStatus === 'in-progress') action.pCardStatus = 'error';
      if (action.postQbStatus === 'in-progress') action.postQbStatus = 'error';
    } finally {
      this.isProcessing = false;
      this.refreshActionableGrid();
    }
  }

  async deliverInvoice(action: InvoicePostAction) {
    if (this.isProcessing) return;
    if (!(await this.assertQbHealthy())) return;
    this.isProcessing = true;
    try {
      await this.invoiceProcessing.deliverInvoiceCore(action, false, false, () => this.refreshActionableGrid());
      if (this.dbSaveService.hasChanges()) await this.dbSaveService.saveChanges();
    } catch (err: any) {
      action.deliverStatus = 'error';
      action.overallStatus = 'error';
      action.errorMessage = err?.message || 'Delivery failed';
    } finally {
      this.isProcessing = false;
      this.refreshActionableGrid();
    }
  }

  async savePdf(action: InvoicePostAction) {
    if (this.isProcessing) return;
    if (!(await this.assertQbHealthy())) return;
    this.isProcessing = true;
    try {
      await this.invoiceProcessing.savePdfCore(action, () => this.refreshActionableGrid());
      if (this.dbSaveService.hasChanges()) await this.dbSaveService.saveChanges();
    } catch (err: any) {
      action.savePdfStatus = 'error';
      action.overallStatus = 'error';
      action.errorMessage = err?.message || 'PDF save failed';
    } finally {
      this.isProcessing = false;
      this.refreshActionableGrid();
    }
  }

  async chargePCard(action: InvoicePostAction) {
    if (this.isProcessing) return;
    if (!(await this.assertQbHealthy())) return;
    this.isProcessing = true;
    try {
      await this.invoiceProcessing.chargePCardCore(action, () => this.refreshActionableGrid());
    } catch (err: any) {
      action.pCardStatus = 'error';
      action.overallStatus = 'error';
      action.errorMessage = err?.message || 'P-Card charge failed';
    } finally {
      this.isProcessing = false;
      this.refreshActionableGrid();
    }
  }

  async postToQb(action: InvoicePostAction) {
    if (this.isProcessing) return;
    if (!(await this.assertQbHealthy())) return;
    this.isProcessing = true;
    try {
      await this.invoiceProcessing.postToQbCore(action, () => this.refreshActionableGrid());
      action.overallStatus = 'success';
      action.processedTs = new Date();
      this.moveActionToHistory(action);
    } catch (err: any) {
      action.postQbStatus = 'error';
      action.overallStatus = 'error';
      action.errorMessage = err?.message || 'QB posting failed';
    } finally {
      this.isProcessing = false;
      this.refreshActionableGrid();
    }
  }

  // -- Helpers --

  private async assertQbHealthy(): Promise<boolean> {
    if (await this.qbHealth.refresh()) return true;
    this.toastr.error(
      'QuickBooks is not connected. Reconnect at QuickBooks Account Setup before continuing.',
      'QuickBooks',
    );
    return false;
  }

  private needsPCardCharge(action: InvoicePostAction): boolean {
    return action.hasPCard && action.pCardStatus !== 'success';
  }

  private moveActionToHistory(action: InvoicePostAction) {
    this.actionableActions = this.actionableActions.filter((a) => a !== action);
    // The history grid is InvoiceHeader-based; push the header into the list so
    // the operator sees the newly-processed invoice without needing to refresh.
    if (!this.historyInvoices.some((i) => i.id === action.invoiceHeader.id)) {
      this.historyInvoices = [action.invoiceHeader, ...this.historyInvoices];
    }
  }

  private refreshActionableGrid() {
    this.actionableGridOptions.apiX?.setGridOption('rowData', this.actionableActions);
    this.actionableGridOptions.apiX?.refreshCells({ force: true });
    this.historyGridOptions.apiX?.setGridOption('rowData', this.historyInvoices);
  }

  private renderPendingStepCell(status: StepStatus, label: string): string {
    if (!status) return '';
    switch (status) {
      case 'n/a':
        return '<span class="text-color-secondary">—</span>';
      case 'pending':
        return label;
      case 'in-progress':
        return `<i class="pi pi-spinner pi-spin mr-1"></i>${label}`;
      case 'success':
        return `<i class="pi pi-check-circle text-green-500 mr-1"></i>${label}`;
      case 'error':
        return '<i class="pi pi-times-circle text-red-500 mr-1"></i>Failed';
      default:
        return '';
    }
  }

  private renderDeliverCell(action: InvoicePostAction): string {
    if (!action) return '';
    const label = this.getDeliveryLabel(action);
    switch (action.deliverStatus) {
      case 'n/a':
        return `<span class="text-color-secondary">${label}</span>`;
      case 'pending':
        return `Will ${label}`;
      case 'in-progress':
        return `<i class="pi pi-spinner pi-spin mr-1"></i>${label}`;
      case 'success':
        return `<i class="pi pi-check-circle text-green-500 mr-1"></i>${label}`;
      case 'error':
        return '<i class="pi pi-times-circle text-red-500 mr-1"></i>Failed';
      default:
        return '';
    }
  }

  private getDeliveryLabel(action: InvoicePostAction): string {
    switch (action.deliveryMethod) {
      case DocSubmitTypeEnum.Print: return 'Print';
      case DocSubmitTypeEnum.Email: return 'Email';
      case DocSubmitTypeEnum.PrintAndEmail: return 'Print & Email';
      case DocSubmitTypeEnum.None: return 'None';
      default: return 'Unknown';
    }
  }

  // -- Voucher creation (existing feature, preserved) --

  @wrapBusy
  async createAccountOwnedVouchers() {
    if (!(await this.qbHealth.refresh())) {
      this.toastr.error(
        'QuickBooks is not connected. Reconnect at QuickBooks Account Setup before continuing.',
        'QuickBooks',
      );
      return;
    }
    const itds = await this.dbQueryService.getItemDetailsForUninvoicedAccountOwnedInventory();
    if (itds.length === 0) {
      this.dialogService.okDialog(
        'No New Account-Owned Vouchers',
        'No received account-owned items are available to voucher.',
      );
      return;
    }

    const grps = _.groupBy(itds, (itd) => itd.accountPurchaseJoDetail.joHeaderId);
    for (const [_johId, invItds] of Object.entries(grps)) {
      const joh = invItds[0].accountPurchaseJoDetail.joHeader;
      await this.createVoucher(joh, invItds);
    }
    await this.loadData();
  }

  async createVoucher(joh: JoHeader, invItds: ItemDetail[]) {
    const account = await this.dbQueryService.getAccount(joh.accountId);
    const invoiceHeaderId = await this.dbQueryService.getNextInvoiceHeaderId(joh.id);
    const invh = {} as InvoiceHeader;
    const now = new Date();
    invh.id = invoiceHeaderId;

    invh.invoiceDate = DateFns.startOfDay(now);
    const appConfig = this.dbQueryService.appConfig;
    const dueDays = joh.account.dueDateNumDays || appConfig.account?.dueDateNumDays || 30;
    invh.dueDate = DateFns.dateAdd(invh.invoiceDate, dueDays, 'days');

    invh.crtnTs = now;
    invh.shipTs = now;

    invh.invoiceStatusId = InvoiceStatusCode.Voucher;
    invh.joHeaderId = joh.id;

    invItds.forEach((itd) => {
      itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessVoucher;
      itd.accountPurchaseInvoiceHeaderId = invoiceHeaderId;
    });

    const invoiceHeader = this.uow.createEntity(InvoiceHeader, invh);
    const joDetails = _.uniq(invItds.map((itd) => itd.accountPurchaseJoDetail));
    joDetails.forEach((jod) => {
      const invd = {} as InvoiceDetail;
      invd.invoiceHeaderId = invoiceHeader.id;
      invd.joDetailId = jod.id;

      const relatedItds = invItds.filter((x) => jod.id == x.accountPurchaseJoDetailId);
      invd.shipQty = relatedItds.length as any;

      this.uow.createEntity(InvoiceDetail, invd);
    });
    invoiceHeader.subtotalAmt = invoiceHeader.calcExtendedAmt() ?? undefined;
    const isTaxExempt = !!invoiceHeader.joHeader.account?.accountTaxExemptionNumber?.trim();
    invoiceHeader.taxAmt = isTaxExempt ? 0 : (invoiceHeader.subtotalAmt || 0) * (invoiceHeader.joHeader.taxRate || 0);
    invoiceHeader.totalAmt = (invoiceHeader.subtotalAmt || 0) + (invoiceHeader.taxAmt || 0);

    await this.dbSaveService.saveChanges();
    this.toastr.success('Voucher(s) Created');
  }
}
